import React, { useState, useEffect, useCallback } from 'react';
import { notification, Select } from 'antd';
import { AppDispatch, RootState } from 'store/store';
import { useDispatch, useSelector } from 'react-redux';
import STATUS from 'constants/status';
import { DATE_TIME_FORMAT } from 'constants/dates';
import dayjs from 'dayjs';
import { OpDivider } from 'components/customAntd/DLS/OpDivider/OpDivider';
import { OpModal } from 'components/customAntd/DLS/OpModal/OpModal';
import { getStatusNameById, getStatusColor, profileIcon } from 'utils/visitorsHelper';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { formatFullName, hasPermission } from 'utils/utils';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { Visitor } from 'types/visitTypes';
import { deleteRequest, getRequest, patchRequest, postRequest } from 'api/apiClient';
import { DenyReason } from 'types/denyTypes';
import { SIGNED_IN_VISITOR_ID, SIGNED_OUT_VISITOR_ID } from 'constants/userActivities';
import { fetchVisits } from 'store/slices/visitsSlice';
import RequiredFieldsModal from './RequiredFieldsModal';
import { VisitorWorkflowFields } from 'types/visitorWorkflowTypes';
import { FormFieldWorkflow, VisitData } from 'types/formFieldTypes';
import { User } from 'types/userTypes';
import ReturnItemsModal from './ReturnItemsModal';
import { searchVisitors, searchVisitorsToday } from 'store/slices/visitorSearchSlice';
import { fetchRedFlags } from 'store/slices/redFlagSlice';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n/i18n';
import Loader from 'components/customAntd/Loader';

interface VisitorsModalProps {
    open: boolean;
    newStatus: number;
    onClose: () => void;
    setLoading: (loading: boolean) => void;
}

const AccessControlModal: React.FC<VisitorsModalProps> = ({ open, newStatus, onClose, setLoading }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const { selectedVisit } = useSelector((state: RootState) => state.visits);
    const lastSearchParams = useSelector((state: RootState) => state.visitorSearch.lastSearchParams);
    const hasRedFlagWrite = hasPermission(tokenScopeList, orgId, 'o', 'redflag:w');

    const [hosts, setHosts] = useState<User[]>([]);
    const [denyReasons, setDenyReasons] = useState<DenyReason[]>([]);
    const [addToBlockList, setAddToBlockList] = useState(false);
    const [addToWatchList, setAddToWatchList] = useState(false);
    const [requiredFieldsModalOpen, setRequiredFieldsModalOpen] = useState(false);
    const [requiredFields, setRequiredFields] = useState<string[]>([]);
    const [checkedVisitors, setCheckedVisitors] = useState<Visitor[]>([]);
    const [signInWorkflowFields, setSignInWorkflowFields] = useState<VisitorWorkflowFields>();
    const [customFieldsWorkflow, setCustomFieldsWorkflow] = useState<FormFieldWorkflow[]>([]);
    const [isDataLoading, setIsDataLoading] = useState(true);

    const [returnItemsModalOpen, setReturnItemsModalOpen] = useState(false);
    const [form] = OpForm.useForm();

    const confirmModal = useConfirmModal();
    const { t } = useTranslation();

    const initialValues = { reasonForDenial: denyReasons[0]?.id };

    const hasVisitorlogWrite = hasPermission(tokenScopeList, orgId, 'o', 'visitorlog:w');
    const hasDenyRead = hasPermission(tokenScopeList, orgId, 'o', 'deny:r');

    useEffect(() => {
        const fetchData = async () => {
            if (!open) return;

            setIsDataLoading(true);

            // Fetch deny reasons only if user has deny:r permission
            if (hasDenyRead) {
                const denyReasonPriority = [
                    t('visitors.accessControlModal.denyReasons.insufficientId'),
                    t('visitors.accessControlModal.denyReasons.blockWatchList'),
                    t('visitors.accessControlModal.denyReasons.byHostTeam'),
                    t('visitors.accessControlModal.denyReasons.appointmentIssues'),
                    t('visitors.accessControlModal.denyReasons.behaviorIssues'),
                    t('visitors.accessControlModal.denyReasons.safetyConcerns'),
                    t('visitors.accessControlModal.denyReasons.other')
                ];

                try {
                    const denyReasonsResponse = await getRequest(`/orgs/${orgId}/denyReason`);
                    const orderedDenyReasons = denyReasonsResponse.data.sort(
                        (a: { name: string }, b: { name: string }) => denyReasonPriority.indexOf(a.name || '') - denyReasonPriority.indexOf(b.name || '')
                    );
                    setDenyReasons(orderedDenyReasons);
                } catch (error) {
                    console.log("Failed to fetch deny reasons.");
                }
            }

            try {
                // Parallel fetch for independent data
                const [hostsResponse, locationDefaultWorkflow, locationVisitorTypeWorkflow] = await Promise.all([
                    getRequest(`/orgs/${orgId}/users`),
                    getRequest(`/orgs/${orgId}/sites/${selectedVisit?.site?.id}/workflows`),
                    getRequest(`/orgs/${orgId}/sites/${selectedVisit?.site.id}/visitorType/${selectedVisit?.visitorType.id}`)
                ]);

                // Process hosts
                const filteredHosts = hostsResponse.data.filter((user: User) => user.status === 1 && (user.hostExclude === 0 || user.hostExclude === null));
                setHosts(filteredHosts);

                // Get workflow ID
                const defaultWorkflowData = locationDefaultWorkflow?.data?.[0];
                const visitorTypeWorkflowData = locationVisitorTypeWorkflow?.data?.[0]?.workflow;

                const signInWorkflowId =
                    visitorTypeWorkflowData?.SignInWorkflowId !== -1
                        ? visitorTypeWorkflowData.SignInWorkflowId
                        : defaultWorkflowData?.SignInWorkflowId;

                // Parallel fetch for workflow fields
                const [signInWorkflowConfigResponse, customFieldsWorkflowResponse] = await Promise.all([
                    getRequest(`/orgs/${orgId}/visitorWorkflow/${signInWorkflowId}/fieldConfig`),
                    getRequest(`/orgs/${orgId}/visitorWorkflow/${signInWorkflowId}/fieldCustom`)
                ]);

                setSignInWorkflowFields(signInWorkflowConfigResponse.data[0]);
                setCustomFieldsWorkflow(customFieldsWorkflowResponse.data);
            } catch (error) {
                console.log("Failed to fetch data.");
            } finally {
                setIsDataLoading(false);
            }
        };
        fetchData();
    }, [orgId, dispatch, selectedVisit?.site.id, selectedVisit?.visitorType.id, t, open, hasDenyRead]);

    const getFilteredVisitors = () => {
        return selectedVisit?.visitors.filter(visitor => {
            if (newStatus === Number(STATUS.SIGNED_IN.id) || newStatus === Number(STATUS.DENIED_ENTRY.id)) {
                return visitor.status === STATUS.PENDING.id;
            } else if (newStatus === Number(STATUS.SIGNED_OUT.id)) {
                return visitor.status === STATUS.SIGNED_IN.id;
            }
            return true;
        });
    };

    const visitorOptions = getFilteredVisitors()?.map(visitor => ({
        label: formatFullName(visitor.firstName, visitor.middleName, visitor.lastName),
        value: `${visitor.id}`,
        firstName: visitor.firstName,
        lastName: visitor.lastName,
        color: getStatusColor(getStatusNameById(visitor.status, selectedVisit?.scheduleStart!))
    }));

    const updateVisitStatus = useCallback(async () => {
        if (newStatus === Number(STATUS.SIGNED_IN.id)) {
            await patchRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}?language=${i18n.language.split('-')[0]}`, {
                status: newStatus,
                signIn: dayjs().format(DATE_TIME_FORMAT),
            });
            notification.success({
                message: t('notification.success'),
                description: t('visitors.accessControlModal.notification.signedInSuccess'),
                placement: 'bottomRight',
            });
        } else if (newStatus === Number(STATUS.SIGNED_OUT.id)) {
            await postRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/visitorExit?language=${i18n.language.split('-')[0]}`, {
                signOut: dayjs().format(DATE_TIME_FORMAT),
            });
            notification.success({
                message: t('notification.success'),
                description: t('visitors.accessControlModal.notification.SignedOutSuccess'),
                placement: 'bottomRight',
            });
        } else if (newStatus === Number(STATUS.DENIED_ENTRY.id)) {
            await patchRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}?language=${i18n.language.split('-')[0]}`, {
                status: newStatus,
                signIn: dayjs().format(DATE_TIME_FORMAT),
            });
            notification.success({
                message: t('notification.success'),
                description: t('visitors.accessControlModal.notification.deniedEntrySuccess'),
                placement: 'bottomRight',
            });
            dispatch(fetchRedFlags({ orgId }));
            setAddToBlockList(false);
            setAddToWatchList(false);
        }
        onClose();
    }, [dispatch, orgId, selectedVisit, newStatus, onClose, t]);

    const updateVisitorStatus = useCallback(async (visitorId: number) => {
        try {
            await patchRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/visitors/${visitorId}`, {
                status: newStatus,
                signIn: newStatus === Number(STATUS.SIGNED_IN.id) || newStatus === Number(STATUS.DENIED_ENTRY.id) ? dayjs().format(DATE_TIME_FORMAT) : undefined,
                signOut: newStatus === Number(STATUS.SIGNED_OUT.id) ? dayjs().format(DATE_TIME_FORMAT) : undefined,
            });
        } catch (error) {
            console.error("Failed to update visitor status.");
        }
    }, [orgId, selectedVisit, newStatus]);

    const handleSubmit = useCallback(async ({ values }: IOnSubmitArgs) => {
        setLoading(true);

        try {
            const visitors = selectedVisit?.visitors.filter(visitor => values.visitorsCheckbox.includes(visitor.id.toString()));

            if (newStatus === Number(STATUS.SIGNED_IN.id)) {
                const visitData = await getRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/visitorData`);

                const requiredFields = signInWorkflowFields
                    ? Object.entries(signInWorkflowFields)
                        .filter(([_, field]) => field.required === 1)
                        .map(([key]) => key)
                    : [];

                // Filter fields without values
                const fields = customFieldsWorkflow.filter((formField: FormFieldWorkflow) => {
                    const matchingVisitData = visitData.data.find(
                        (data: VisitData) => data.formField.id === formField.id && data.value !== null && data.value !== ""
                    );
                    return formField.required === 1 && !matchingVisitData; // Keep only required fields without values
                });

                const isMissingRequiredFields =
                    (requiredFields.includes('visitCompany') && !selectedVisit?.company) ||
                    ((requiredFields.includes('host')) && !selectedVisit?.host.userId && hosts.length > 0) ||
                    (requiredFields.includes('visitPurpose') && !selectedVisit?.purpose) ||
                    visitors?.some((visitor) =>
                        (requiredFields.includes('middleName') && !visitor.middleName) ||
                        (requiredFields.includes('email') && !visitor.email) ||
                        (requiredFields.includes('visitPhone') && !visitor.mobilePhone)
                    ) || fields.length > 0;

                if (isMissingRequiredFields) {
                    setRequiredFields(requiredFields);
                    setRequiredFieldsModalOpen(true);
                    if (visitors) {
                        setCheckedVisitors(visitors);
                    }
                    form.resetFields();
                    setLoading(false);
                    return;
                }
            } else if (newStatus === Number(STATUS.SIGNED_OUT.id)) {
                // Parallel fetch for sign out workflows
                const [locationDefaultWorkflow, locationVisitorTypeWorkflow] = await Promise.all([
                    getRequest(`/orgs/${orgId}/sites/${selectedVisit?.site?.id}/workflows`),
                    getRequest(`/orgs/${orgId}/sites/${selectedVisit?.site.id}/visitorType/${selectedVisit?.visitorType.id}`)
                ]);

                const defaultWorkflowData = locationDefaultWorkflow?.data?.[0];
                const visitorTypeWorkflowData = locationVisitorTypeWorkflow?.data?.[0]?.workflow;

                const SignOutWorkflowId =
                    visitorTypeWorkflowData?.SignOutWorkflowId !== -1
                        ? visitorTypeWorkflowData.SignOutWorkflowId
                        : defaultWorkflowData?.SignOutWorkflowId;

                const signOutWorkflowConfigResponse = await getRequest(`/orgs/${orgId}/visitorWorkflowConfig/${SignOutWorkflowId}`);

                if (signOutWorkflowConfigResponse.data[0].confirmReturnItems === 1) {
                    setReturnItemsModalOpen(true);
                    if (visitors) {
                        setCheckedVisitors(visitors);
                    }
                    form.resetFields();
                    setLoading(false);
                    return;
                }
            }

            // Main submission logic
            try {
                // Deny Entry
                if (newStatus === Number(STATUS.DENIED_ENTRY.id)) {
                    try {
                        for (const visitorId of values.visitorsCheckbox) {
                            await postRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/denyEntry`, {
                                visitorId: Number(visitorId),
                                deniedBy: Number(globalUserId),
                                reasonId: Number(values.reasonForDenial),
                                details: values.detail,
                            });
                        }

                        // Create Red Flag API Call
                        if (values.addToBlockList || values.addToWatchList) {
                            await postRequest(`/orgs/${orgId}/redflag`, {
                                level: values.addToBlockList ? "1" : "2",
                                effectiveDate: dayjs().format(DATE_TIME_FORMAT),
                                visitors: values.visitorsCheckbox.map((visitorId: number) => {
                                    const visitor = selectedVisit?.visitors.find(v => Number(v.id) === Number(visitorId));
                                    return {
                                        firstName: visitor?.firstName,
                                        lastName: visitor?.lastName,
                                        middleName: visitor?.middleName,
                                    };
                                })
                            })
                        };

                        const uncheckedVisitors = selectedVisit?.visitors.filter(
                            (visitor) => !values.visitorsCheckbox.includes(visitor.id.toString())
                        );

                        const allUncheckedDenied = uncheckedVisitors?.every(
                            (visitor) => visitor.status === STATUS.DENIED_ENTRY.id
                        );

                        if (allUncheckedDenied) {
                            await deleteRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/msiAltaGuestPass`);
                        }
                    } catch (error) {
                        console.error("Failed in denyEntry or createRedFlag.");
                    }
                }

                // update status of visit and visitors - parallel processing
                const visitorUpdatePromises = visitors?.map(visitor =>
                    visitor.id ? updateVisitorStatus(visitor.id) : Promise.resolve()
                ) || [];

                await Promise.all([
                    ...visitorUpdatePromises,
                    updateVisitStatus()
                ]);

                // log activity
                const fullNameList = visitors?.map((visitor: Visitor) => formatFullName(visitor.firstName, visitor.middleName, visitor.lastName,)).join(', ');
                const logActivityPromise = (newStatus === Number(STATUS.SIGNED_IN.id))
                    ? postRequest(`/orgs/${orgId}/userActivity`, {
                        userId: globalUserId,
                        activityId: SIGNED_IN_VISITOR_ID,
                        details: fullNameList
                    })
                    : (newStatus === Number(STATUS.SIGNED_OUT.id))
                        ? postRequest(`/orgs/${orgId}/userActivity`, {
                            userId: globalUserId,
                            activityId: SIGNED_OUT_VISITOR_ID,
                            details: fullNameList
                        })
                        : Promise.resolve();

                // Parallel dispatch and activity logging
                const dispatchPromises = [
                    dispatch(fetchVisits({ orgId })),
                    dispatch(searchVisitorsToday({ orgId })),
                    logActivityPromise
                ];

                if (Object.keys(lastSearchParams).length !== 0) {
                    dispatchPromises.push(
                        dispatch(searchVisitors({
                            ...lastSearchParams,
                            orgId,
                            siteId: lastSearchParams.siteId || globalLocationId!
                        }))
                    );
                }

                await Promise.all(dispatchPromises);

                form.resetFields();
            } catch (error) {
                console.error('Submission failed:', error);
                notification.error({
                    message: t('notification.error'),
                    description: 'Failed to update visitor status. Please try again.',
                    placement: 'bottomRight',
                });
            } finally {
                setLoading(false);
            }
        } catch (error) {
            console.error('Validation failed:', error);
            setLoading(false);
        }
    }, [orgId, globalUserId, form, selectedVisit, newStatus, dispatch, updateVisitStatus, updateVisitorStatus, lastSearchParams, globalLocationId, signInWorkflowFields, customFieldsWorkflow, hosts.length, t, setLoading]);

    const onOkHandler = async () => {
        const selectedVisitors = form.getFieldValue('visitorsCheckbox') || [];
        if (selectedVisitors.length === 0) {
            notification.warning({
                message: t('visitors.accessControlModal.notification.noVisitorsSelected.title'),
                description: t('visitors.accessControlModal.notification.noVisitorsSelected.description'),
                placement: 'bottomRight',
            });
            return;
        }
        form.submit();
        onClose();
    };

    const getTitle = () => {
        switch (newStatus) {
            case Number(STATUS.SIGNED_IN.id):
                return t('visitors.accessControlModal.title.signIn');
            case Number(STATUS.SIGNED_OUT.id):
                return t('visitors.accessControlModal.title.signOut');
            case Number(STATUS.DENIED_ENTRY.id):
                return t('visitors.accessControlModal.title.denyEntry');
            default:
                return '';
        }
    };

    const handleBlockListChange = (checked: boolean) => {
        setAddToBlockList(checked);
        if (checked) {
            setAddToWatchList(false);
            form.setFieldsValue({ addToWatchList: false });
        }
    };

    const handleWatchListChange = (checked: boolean) => {
        setAddToWatchList(checked);
        if (checked) {
            setAddToBlockList(false);
            form.setFieldsValue({ addToBlockList: false });
        }
    };

    const finalOnCancel = () => {
        if (form.isFieldsTouched()) {
            confirmModal({
                title: t('confirmDiscard.title'),
                content: t('confirmDiscard.message'),
                okText: t('confirmDiscard.ok'),
                cancelText: t('confirmDiscard.cancel'),
                onOk: () => {
                    form.resetFields();
                    setAddToBlockList(false);
                    setAddToWatchList(false);
                    onClose();
                },
            });
        } else {
            setAddToBlockList(false);
            setAddToWatchList(false);
            onClose();
        }
    };

    return (
        <>
            <OpModal
                title={getTitle()}
                open={open}
                onOk={onOkHandler}
                onCancel={isDataLoading ? undefined : finalOnCancel}
                centered
                closable={!isDataLoading}
                footer={isDataLoading ? null : undefined}
            >
                {isDataLoading ? (
                    <Loader />
                ) : (
                    <>
                        <OpDivider />

                        <OpForm
                            form={form}
                            initialValues={initialValues}
                            onSubmit={handleSubmit}
                            hasError={false}
                            defaultButtons={false}
                        >
                            <OpForm.CheckboxGroup name="visitorsCheckbox" >
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    {visitorOptions?.map(option => (
                                        <OpForm.Checkbox key={option.value} value={option.value} style={{ marginRight: '8px' }}>
                                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                                {profileIcon({ firstName: option.firstName || "", lastName: option.lastName || "", size: 28, color: option.color })}
                                                <span style={{ marginLeft: '8px' }}>{option.label}</span>
                                            </span>
                                        </OpForm.Checkbox>
                                    ))}
                                </div>
                            </OpForm.CheckboxGroup>

                            {newStatus === Number(STATUS.DENIED_ENTRY.id) && (
                                <>
                                    <OpForm.Item label={t('visitors.accessControlModal.reasonForDenial')} name="reasonForDenial">
                                        <Select
                                            options={denyReasons.map(reason => ({
                                                label: t(reason.languageCode ?? '', reason.name ?? ''),
                                                value: reason.id
                                            }))}
                                        />
                                    </OpForm.Item>
                                    {(hasVisitorlogWrite) && (
                                        <OpForm.TextAreaInput label={t('visitors.accessControlModal.details')} name="detail" rows={4} />
                                    )}

                                    {hasRedFlagWrite && (
                                        <>
                                            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                <OpForm.Switch name="addToBlockList" style={{ marginBottom: 0 }} checked={addToBlockList} onChange={handleBlockListChange} />
                                                <span style={{ marginLeft: 8 }}>{t('visitors.accessControlModal.addToBlockList')}</span>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                <OpForm.Switch name="addToWatchList" style={{ marginBottom: 0 }} checked={addToWatchList} onChange={handleWatchListChange} />
                                                <span style={{ marginLeft: 8 }}>{t('visitors.accessControlModal.addToWatchList')}</span>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </OpForm>
                    </>
                )}
            </OpModal>
            {(requiredFieldsModalOpen) && (
                <RequiredFieldsModal
                    open={requiredFieldsModalOpen}
                    requiredFields={requiredFields}
                    checkedVisitors={checkedVisitors}
                    onClose={() => setRequiredFieldsModalOpen(false)}
                    updateVisitStatus={updateVisitStatus}
                    updateVisitorStatus={updateVisitorStatus}
                    setDrawerLoading={setLoading}
                />
            )}
            {(returnItemsModalOpen) && (
                <ReturnItemsModal
                    open={returnItemsModalOpen}
                    checkedVisitors={checkedVisitors}
                    onClose={() => setReturnItemsModalOpen(false)}
                    updateVisitorStatus={updateVisitorStatus}
                    setLoading={setLoading}
                />
            )}
        </>
    );
};

export default AccessControlModal;